import { ProcessingOverlay } from '@components'

import {
  BorderRadius,
  Button,
  ButtonsGroup,
  Color,
  Divider,
  Flex,
  Input,
  Modal,
  ModalProps,
  Space,
  Textarea,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { ZervantLocale } from '@features/invoices/enums/zervantLocale'
import { useFetchInvoiceEmailTemplate } from '@features/invoices/hooks/useFetchInvoiceEmailTemplate'
import { getDocumentName } from '@features/invoices/utils/getDocumentName'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { ModalId } from '../../../../enums/modalId'
import { useForm } from '../../../../hooks'
import { DEMO_INVOICE_NUMBER, DEMO_INVOICE_NUMBER_PREFIX } from '../../constants/demoInvoiceNumber'
import { useDemoInvoiceFakeSend } from '../../hooks/useDemoInvoiceFakeSend'
import { getDemoInvoiceSendAsEmailFormSchema } from '../../utils/getDemoInvoiceSendAsEmailFormSchema'
import * as Styled from './styles'

interface DemoInvoiceSendAsEmailProps extends ModalProps {
  onSent: () => void
}

const DemoInvoiceSendAsEmail = ({ onSent, ...modalProps }: DemoInvoiceSendAsEmailProps): ReactElement => {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const { organization } = useUserOrganization()
  const { close } = useModal(ModalId.DemoInvoiceSendAsEmailModal)
  const { invoiceEmailTemplate, isLoading } = useFetchInvoiceEmailTemplate(
    InvoiceDocumentType.Invoice,
    ZervantLocale.EN_GB,
  )
  const { fakeSend, isSending } = useDemoInvoiceFakeSend({
    modalId: ModalId.DemoInvoiceSendAsEmailModal,
    onSuccess: onSent,
  })

  const validationSchema = useMemo(() => getDemoInvoiceSendAsEmailFormSchema(t), [t])
  const emailSubject = getDocumentName({
    documentType: InvoiceDocumentType.Invoice,
    invoiceNumber: getInvoiceNumberDisplayedValue(DEMO_INVOICE_NUMBER, DEMO_INVOICE_NUMBER_PREFIX),
    organizationName: organization?.name,
    t,
  })
  const emailMessage = invoiceEmailTemplate?.body?.replace(/<br>|<br\/>/g, '\n')
  const emailRecipient = user?.email ?? ''

  const { Form, FormItem, handleSubmit, reset } = useForm({
    defaultValues: { emailMessage, emailRecipient, emailSubject },
    validationSchema,
  })

  useEffect(() => {
    if (!isLoading) {
      reset({ emailMessage, emailRecipient, emailSubject })
    }
  }, [emailMessage, emailRecipient, emailSubject, isLoading, reset])

  const handleSendButtonClick = useCallback(() => {
    handleSubmit(fakeSend)()
  }, [fakeSend, handleSubmit])

  return (
    <Modal {...modalProps} closable={!isSending} size="s">
      <Modal.Header title={t('external_invoices.send_as_email.modal.title.send_document')} />
      <Modal.Body>
        <Styled.ModalBodyWrapper>
          {(isLoading || isSending) && (
            <ProcessingOverlay borderRadius={BorderRadius.R16} color={Color.White} withAnimation />
          )}
          <Form>
            <FormItem
              label={t('external_invoices.send_as_email.modal.form.field.recipients.label')}
              name="emailRecipient"
              render={({ field: { onChange, ...rest } }) => <Input {...rest} disabled size="sm" />}
            />
            <Space />
            <Divider />
            <Space />
            <FormItem
              label={t('external_invoices.send_as_email.modal.form.field.subject.label')}
              name="emailSubject"
              render={({ field }) => <Input {...field} size="sm" />}
            />
            <Space />
            <FormItem
              label={t('external_invoices.send_as_email.modal.form.field.message.label')}
              name="emailMessage"
              render={({ field }) => (
                <Textarea
                  {...field}
                  expandOnType
                  maxHeight={160}
                  placeholder={t('external_invoices.send_as_email.modal.form.field.message.placeholder')}
                  resizable={false}
                  rows={5}
                  size="sm"
                />
              )}
            />
          </Form>
        </Styled.ModalBodyWrapper>
      </Modal.Body>
      <Modal.Footer>
        <Flex alignItems="center" flexGrow={1} justifyContent="flex-end">
          <ButtonsGroup>
            <Button disabled={isLoading || isSending} onClick={close} variant="text">
              {t('cancel')}
            </Button>
            <Button disabled={isLoading || isSending} loading={isSending} onClick={handleSendButtonClick}>
              {t('external_invoices.send_as_email.modal.action.send_now')}
            </Button>
          </ButtonsGroup>
        </Flex>
      </Modal.Footer>
    </Modal>
  )
}

export const DemoInvoiceSendAsEmailModal = withModalConditionalRender(DemoInvoiceSendAsEmail)
